import { easeQuadInOut } from 'd3-ease';
import React, { useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { animated, useSpring } from 'react-spring/web.cjs';
import styled, { css } from 'styled-components';

import { NAVBAR_BASE_HEIGHT } from '../../../../constants/ui/navbar';
import { getAppDOMElement, getPortalRootDOMElement } from '../../../../DOMElements';
import { ZIndices } from '../../../../styled-components/dark/common';
import MissionDetailsContainer from '../MissionDetails/MissionDetailsContainer';

const APPEARANCE_TRANSITION_DURATION = 300;

type AnimatedWrapperType = {
  $show: boolean;
};

const AnimatedWrapper = styled(animated.div)<AnimatedWrapperType>`
  position: absolute;
  padding: 0;
  margin: 0;
  width: 100%;
  height: calc(100% - ${NAVBAR_BASE_HEIGHT}em);
  height: calc(100% - ${NAVBAR_BASE_HEIGHT}em - constant(safe-area-inset-top));
  height: calc(100% - ${NAVBAR_BASE_HEIGHT}em - env(safe-area-inset-top));
  bottom: 0px;

  left: 0;
  z-index: ${ZIndices.missionsDetailsModal};
  transition: opacity 300ms ease-in-out;

  ${({ $show }) =>
    $show
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0;
          pointer-events: none;
        `}
`;

type MobileMissionDetailsModalProps = {
  isOpen: boolean;
  missionId: number;
};

const MobileMissionDetailsModal = ({ isOpen = false, missionId }: MobileMissionDetailsModalProps) => {
  const [wrapperHeight, setWrapperHeight] = useState(0);
  const [delayedMissionId, setDelayedMissionId] = useState(missionId);

  useLayoutEffect(() => {
    const app = getAppDOMElement();

    if (app) {
      setWrapperHeight(
        parseFloat(
          `${
            app.clientHeight -
            parseFloat(window.getComputedStyle(app).paddingTop) -
            parseFloat(window.getComputedStyle(app).paddingBottom)
          }`,
        ) || 0,
      );
    }
  }, []);

  // @ts-ignore
  const [{ y }, set] = useSpring(
    () => ({
      config: {
        duration: APPEARANCE_TRANSITION_DURATION,
        easing: easeQuadInOut,
      },
    }),
    [],
  );

  useLayoutEffect(() => {
    if (typeof wrapperHeight === 'number') {
      // @ts-ignore
      set(() => ({ y: isOpen ? 0 : wrapperHeight + 100 }));
    }
  }, [isOpen, set, wrapperHeight, missionId]);

  const missionIdRefTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useLayoutEffect(() => {
    missionIdRefTimeoutRef.current = setTimeout(() => {
      setDelayedMissionId(missionId);
      missionIdRefTimeoutRef.current = null;
    }, APPEARANCE_TRANSITION_DURATION);
  }, [missionId]);

  useLayoutEffect(() => {
    return () => {
      if (missionIdRefTimeoutRef.current) {
        clearTimeout(missionIdRefTimeoutRef.current);
      }
    };
  }, []);

  return createPortal(
    <AnimatedWrapper style={{ y }} $show={isOpen}>
      <MissionDetailsContainer missionId={delayedMissionId} />
    </AnimatedWrapper>,
    getPortalRootDOMElement(),
  );
};

export default MobileMissionDetailsModal;
